import React, { ReactNode, useCallback, useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Icon,
  Icons,
  Modal,
  Overlay,
  OverlayBackdrop,
  OverlayCenter,
  Spinner,
  Text,
  Tooltip,
  TooltipProvider,
  as,
} from 'folds';
import FileSaver from 'file-saver';
import { EncryptedAttachmentInfo } from 'browser-encrypt-attachment';
import FocusTrap from 'focus-trap-react';
import { MsgType, IContent, Room, } from 'matrix-js-sdk';
import { useTranslation } from 'react-i18next';
import { IFileInfo } from '../../../../types/matrix/common';
import { AsyncState, AsyncStatus, useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useMatrixClient } from '../../../hooks/useMatrixClient';
import { bytesToSize } from '../../../utils/common';
import {
  READABLE_EXT_TO_MIME_TYPE,
  READABLE_TEXT_MIME_TYPES,
  getFileNameExt,
  mimeTypeToExt,
} from '../../../utils/mimeTypes';
import * as css from './style.css';
import { stopPropagation } from '../../../utils/keyboard';
import {
  decryptFile,
  downloadEncryptedMedia,
  downloadMedia,
  mxcUrlToHttp,
} from '../../../utils/matrix';
import { useMediaAuthentication } from '../../../hooks/useMediaAuthentication';
import { useTypingStatusUpdater } from '../../../hooks/useTypingStatusUpdater';

import { CurveCmdDocSwitch, CurveMakeCmd } from '../../../utils/CurveCmds'

const RenderErrorButton = ({ retry, text }: { retry: () => void, text: string }) => (
  <TooltipProvider
    tooltip={
      <Tooltip variant="Critical">
        <Text>{text}</Text>
      </Tooltip>
    }
    position="Top"
    align="Center"
  >
    {(triggerRef) => (
      <Button
        ref={triggerRef}
        size="400"
        variant="Critical"
        fill="Soft"
        outlined
        radii="300"
        onClick={retry}
        before={<Icon size="100" src={Icons.Warning} filled />}
      >
        <Text size="B400" truncate>
          {text}
        </Text>
      </Button>
    )}
  </TooltipProvider>
);

type RenderTextViewerProps = {
  name: string;
  text: string;
  langName: string;
  requestClose: () => void;
};
type ReadTextFileProps = {
  body: string;
  mimeType: string;
  url: string;
  encInfo?: EncryptedAttachmentInfo;
  renderViewer: (props: RenderTextViewerProps) => ReactNode;
};
export function ReadTextFile({ body, mimeType, url, encInfo, renderViewer }: ReadTextFileProps) {
  const { t } = useTranslation();
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const [textViewer, setTextViewer] = useState(false);

  const [textState, loadText] = useAsyncCallback(
    useCallback(async () => {
      const mediaUrl = mxcUrlToHttp(mx, url, useAuthentication) ?? url;
      const fileContent = encInfo
        ? await downloadEncryptedMedia(mediaUrl, (encBuf) => decryptFile(encBuf, mimeType, encInfo))
        : await downloadMedia(mediaUrl);

      const text = fileContent.text();
      setTextViewer(true);
      return text;
    }, [mx, useAuthentication, mimeType, encInfo, url])
  );

  return (
    <>
      {textState.status === AsyncStatus.Success && (
        <Overlay open={textViewer} backdrop={<OverlayBackdrop />} className={css.OverlayModalWide}>
          <OverlayCenter>
            <FocusTrap
              focusTrapOptions={{
                initialFocus: false,
                onDeactivate: () => setTextViewer(false),
                clickOutsideDeactivates: true,
                escapeDeactivates: stopPropagation,
              }}
            >
              <Modal
                className={css.ModalWide}
                size="500"
                onContextMenu={(evt: any) => evt.stopPropagation()}
              >
                {renderViewer({
                  name: body,
                  text: textState.data,
                  langName: READABLE_TEXT_MIME_TYPES.includes(mimeType)
                    ? mimeTypeToExt(mimeType)
                    : mimeTypeToExt(READABLE_EXT_TO_MIME_TYPE[getFileNameExt(body)] ?? mimeType),
                  requestClose: () => setTextViewer(false),
                })}
              </Modal>
            </FocusTrap>
          </OverlayCenter>
        </Overlay>
      )}
      {textState.status === AsyncStatus.Error ? (
        <RenderErrorButton retry={loadText} text={t('FileContent.failed_to_load')} />
      ) : (
        <Button
          variant="Secondary"
          fill="Solid"
          radii="300"
          size="400"
          onClick={() =>
            textState.status === AsyncStatus.Success ? setTextViewer(true) : loadText()
          }
          disabled={textState.status === AsyncStatus.Loading}
          before={
            textState.status === AsyncStatus.Loading ? (
              <Spinner fill="Solid" size="100" variant="Secondary" />
            ) : (
              <Icon size="100" src={Icons.ArrowRight} filled />
            )
          }
        >
          <Text size="B400" truncate>
            {t('FileContent.open_file')}
          </Text>
        </Button>
      )}
    </>
  );
}

type RenderPdfViewerProps = {
  name: string;
  src: string;
  requestClose: () => void;
};

export interface RefofReadPdfFile {
  msgId: string | undefined,
  pdfViewer: boolean,
  body: string,
  requestClose: () => void,
  pdfState: AsyncState<string, unknown>,
  renderViewer: (props: RenderPdfViewerProps) => ReactNode,
  remoteSwitcher: () => void,
};

export type ReadPdfFileProps = {
  body: string;
  mimeType: string;
  url: string;
  encInfo?: EncryptedAttachmentInfo;
  room?: Room;
  msgId?: string;
  renderViewer: (props: RenderPdfViewerProps) => ReactNode;
  setShowDocArea: React.Dispatch<React.SetStateAction<boolean>>;
  mid: string;
  setMid: React.Dispatch<React.SetStateAction<string>>;
  setDocInfo: React.Dispatch<React.SetStateAction<unknown>>;
};


export const ReadPdfFile = forwardRef<RefofReadPdfFile, ReadPdfFileProps>(({ body, mimeType, url, encInfo, msgId, room, renderViewer, setShowDocArea, mid, setMid, setDocInfo }, ref) => {
  const { t } = useTranslation();
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const [pdfViewer, setPdfViewer] = useState(false);

  const [pdfState, loadPdf] = useAsyncCallback(
    useCallback(async () => {
      const mediaUrl = mxcUrlToHttp(mx, url, useAuthentication) ?? url;
      const fileContent = encInfo
        ? await downloadEncryptedMedia(mediaUrl, (encBuf) => decryptFile(encBuf, mimeType, encInfo))
        : await downloadMedia(mediaUrl);
      setPdfViewer(true);
      return URL.createObjectURL(fileContent);
    }, [mx, url, useAuthentication, mimeType, encInfo])
  );

  const nowRef = useRef(null);
  const [focusUpdate, setFocusUpdate] = useState(true);
  const sendTypingStatus = useTypingStatusUpdater(mx, room?.roomId);

  const sendDocSwitchCmd = useCallback(() => {
    const plainText = CurveMakeCmd(`${CurveCmdDocSwitch}{"ID":"${msgId}"}`);
    const msgType = MsgType.Text;

    const content: IContent = {
      msgtype: msgType,
      body: plainText,
    };
    if (room) {
      mx.sendMessage(room.roomId, content);
      sendTypingStatus(false);
    }
  }, [mx, room, msgId, sendTypingStatus]);

  useImperativeHandle(ref, useCallback(() => ({
    msgId,
    pdfViewer,
    body,
    requestClose: () => {setPdfViewer(false); setShowDocArea(false);},
    pdfState,
    renderViewer,
    remoteSwitcher: () => {setShowDocArea(true); setPdfViewer(true); setFocusUpdate(!focusUpdate);},
  }),[msgId, pdfViewer, body, pdfState, renderViewer, setShowDocArea, focusUpdate]));

  useEffect(() => {
    setPdfViewer(false);
    if (pdfState.status === AsyncStatus.Success) setPdfViewer(true);
    else loadPdf();
  }, [pdfState.status]);


  useEffect(() => {
    if(mid === msgId) {
      if (setDocInfo) {
        setDocInfo({
          msgId,
          pdfViewer,
          body,
          requestClose: () => {setPdfViewer(false); setShowDocArea(false);},
          pdfState,
          renderViewer,
          remoteSwitcher: () => {setShowDocArea(true); setPdfViewer(true); setFocusUpdate(!focusUpdate);},
        })
      }
    }
  },[focusUpdate, msgId]);

  /* obsoleted function
  useEffect(() => {
    const docs = [...document.getElementsByClassName('DocPopUp')] as HTMLElement[];
    docs.forEach((doc)=>{doc.style.zIndex='';});
    if(nowRef.current) nowRef.current.style.zIndex = '10000';
    // setFocusZIndex('0');
  },[focusUpdate]);
  */ 

  return (
    <>
      {/*pdfState.status === AsyncStatus.Success && (
        <Overlay ref={nowRef} open={pdfViewer} className={`${css.OverlayModalWide} DocPopUp ${msgId}` } >
              <Modal
                className={css.ModalWide}
                size="500"
                onContextMenu={(evt: any) => evt.stopPropagation()}
              >
                {renderViewer({
                  name: body,
                  src: pdfState.data,
                  requestClose: () => {setPdfViewer(false); setShowDocArea(false);},
                })}
              </Modal>
        </Overlay>
      )*/}
      {pdfState.status === AsyncStatus.Error ? (
        <RenderErrorButton retry={loadPdf} text={t('FileContent.failed_to_load')} />
      ) : (
        <Button
          variant="Secondary"
          fill="Solid"
          radii="300"
          size="400"
          onClick={() => {
            if(pdfState.status === AsyncStatus.Success) {
              setShowDocArea(true);
              setMid(`${msgId}`);
              setPdfViewer(true);
              setFocusUpdate(!focusUpdate);
              sendDocSwitchCmd();
            } else
              loadPdf();
            }}
          disabled={pdfState.status === AsyncStatus.Loading}
          before={
            pdfState.status === AsyncStatus.Loading ? (
              <Spinner fill="Solid" size="100" variant="Secondary" />
            ) : (
              <Icon size="100" src={Icons.ArrowRight} filled />
            )
          }
        >
          <Text size="B400" truncate>
            {t('FileContent.open_pdf')}
          </Text>
        </Button>
      )}
    </>
  );
});

export type DownloadFileProps = {
  body: string;
  mimeType: string;
  url: string;
  info: IFileInfo;
  encInfo?: EncryptedAttachmentInfo;
};
export function DownloadFile({ body, mimeType, url, info, encInfo }: DownloadFileProps) {
  const { t } = useTranslation();
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();

  const [downloadState, download] = useAsyncCallback(
    useCallback(async () => {
      const mediaUrl = mxcUrlToHttp(mx, url, useAuthentication) ?? url;
      const fileContent = encInfo
        ? await downloadEncryptedMedia(mediaUrl, (encBuf) => decryptFile(encBuf, mimeType, encInfo))
        : await downloadMedia(mediaUrl);

      const fileURL = URL.createObjectURL(fileContent);
      FileSaver.saveAs(fileURL, body);
      return fileURL;
    }, [mx, url, useAuthentication, mimeType, encInfo, body])
  );

  return downloadState.status === AsyncStatus.Error ? (
    <RenderErrorButton retry={download} text={t('FileContent.retry_download', { size: bytesToSize(info.size ?? 0) })} />
  ) : (
    <Button
      variant="Secondary"
      fill="Soft"
      radii="300"
      size="400"
      onClick={() =>
        downloadState.status === AsyncStatus.Success
          ? FileSaver.saveAs(downloadState.data, body)
          : download()
      }
      disabled={downloadState.status === AsyncStatus.Loading}
      before={
        downloadState.status === AsyncStatus.Loading ? (
          <Spinner fill="Soft" size="100" variant="Secondary" />
        ) : (
          <Icon size="100" src={Icons.Download} filled />
        )
      }
    >
      <Text size="B400" truncate>{t('FileContent.download', { size: bytesToSize(info.size ?? 0) })}</Text>
    </Button>
  );
}

type FileContentProps = {
  body: string;
  mimeType: string;
  renderAsTextFile: () => ReactNode;
  renderAsPdfFile: () => ReactNode;
};
export const FileContent = as<'div', FileContentProps>(
  ({ body, mimeType, renderAsTextFile, renderAsPdfFile, children, ...props }, ref) => (
    <Box direction="Column" gap="300" {...props} ref={ref}>
      {(READABLE_TEXT_MIME_TYPES.includes(mimeType) ||
        READABLE_EXT_TO_MIME_TYPE[getFileNameExt(body)]) &&
        renderAsTextFile()}
      {mimeType === 'application/pdf' && renderAsPdfFile()}
      {children}
    </Box>
  )
);
